import * as React from 'react'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import { Payload as LegendPayload } from 'recharts/types/component/DefaultLegendContent'
import { IconButton } from '@/components/common/IconButton/IconButton'
import { VarianceBadge } from '@/components/common/VarianceBadge/VarianceBadge'
import { useIsScrolled, UsageCostLabelsMap } from '@/hooks'
import { formatNumber } from '@/utils/formatValueWithUnit'
import { getVarianceDataSetPreviousLabel } from '@/utils/variance'
import { getDataSetSummary } from './getDataSetSummary'
import { DataCompletenessListItem } from './DataCompletenessListItem'
import type { DataCompleteness } from '../../hooks/useUsageAndCostDataCompleteness'
import { useAnalyticsFiltersStore } from '@/features/Analytics/stores/useAnalyticsFiltersStore/useAnalyticsFiltersStore'
import { parseTimelineFilter } from '@/components/common/Filter/TimelineFilter/utils'
import dayjs from 'dayjs'

export type DataSummaryDropdownContentProps = {
    onMouseEnter: () => void
    onMouseLeave: () => void
    onClose: () => void
}

type UsageAndCostSummaryDropdownContentProps =
    DataSummaryDropdownContentProps & {
        payload: LegendPayload[]
        data: Array<Record<string, unknown>>
        labelsMap: UsageCostLabelsMap
        hasDataVariance: boolean
        totalUsns: number
        dataCompletenessMap: Map<number, DataCompleteness>
    }

type SummaryDataItem = {
    color?: string
    dataKey: string
    currentDataSetSummary: number
    previousDataSetSummary: number
}

export const UsageAndCostSummaryDropdownContent: React.FC<
    UsageAndCostSummaryDropdownContentProps
> = ({
    payload,
    data,
    labelsMap,
    hasDataVariance,
    totalUsns,
    dataCompletenessMap,
    onMouseEnter,
    onMouseLeave,
    onClose,
}) => {
    const scrollableRef = React.useRef<HTMLUListElement>(null)
    const isScrolled = useIsScrolled(scrollableRef)
    const { t } = useTranslation('translations')

    const activeFilters = useAnalyticsFiltersStore(
        (state) => state.activeFilters
    )

    const summaryData = React.useMemo<SummaryDataItem[]>(() => {
        return payload.map((entry) => {
            const dataKey =
                (entry.payload as unknown as { dataKey?: string }).dataKey ||
                (entry.value as string)
            const label = getVarianceDataSetPreviousLabel({
                dataKey,
                labelsMap,
            })

            const currentDataSetSummary = getDataSetSummary(data, dataKey)
            const previousDataSetSummary = label
                ? getDataSetSummary(data, label)
                : 0

            return {
                color: entry.color,
                dataKey,
                currentDataSetSummary,
                previousDataSetSummary,
            }
        })
    }, [payload, data, labelsMap])

    const { instancesWithData, totalInstances, completenessPercentage } =
        React.useMemo(() => {
            let sumReportedUsns = 0
            for (const completeness of dataCompletenessMap.values()) {
                sumReportedUsns += completeness.usnsWithData
            }

            const timelineValue = parseTimelineFilter(activeFilters.timeline)
            const startDate = dayjs(timelineValue.startDate)
            const endDate = dayjs(timelineValue.endDate)

            // Round up when the range includes any partial month; always count at least 1 month
            const exactMonths = endDate.diff(startDate, 'month', true)
            const numberOfMonths = Math.max(1, Math.ceil(exactMonths))

            const totalInst = totalUsns * numberOfMonths

            const percentage =
                totalInst > 0
                    ? Math.round((sumReportedUsns / totalInst) * 100)
                    : 0

            return {
                instancesWithData: sumReportedUsns,
                totalInstances: totalInst,
                completenessPercentage: percentage,
            }
        }, [dataCompletenessMap, totalUsns, activeFilters.timeline])

    const showVarianceBadge = summaryData.some(
        ({ currentDataSetSummary, previousDataSetSummary }) =>
            hasDataVariance && currentDataSetSummary && previousDataSetSummary
    )

    const COLUMNTEMPLATE_WITH_VARIANCE = 'auto auto auto auto'
    const COLUMNTEMPLATE_NO_VARIANCE = 'auto auto auto'

    const columnsTemplate = showVarianceBadge
        ? COLUMNTEMPLATE_WITH_VARIANCE
        : COLUMNTEMPLATE_NO_VARIANCE

    const headerClassName = classNames('px-4', {
        'border-b border-b-neutral-200': isScrolled,
    })

    return (
        <div
            data-testid="summary-dropdown-content"
            className="py-1"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            role="tooltip"
        >
            <div
                data-testid="summary-dropdown-content-header"
                className={headerClassName}
            >
                <div className="mb-1 flex items-center justify-between gap-2">
                    <h3
                        data-testid="summary-dropdown-content-header-title"
                        className="text-left font-raleway text-sm font-bold leading-6"
                    >
                        {t('analytics.summary_dropdown.list.title')}
                    </h3>
                    <IconButton
                        icon="close"
                        size="medium"
                        name="summary-dropdown-content-header-close-button"
                        variant="ghostSecondary"
                        onClick={onClose}
                    />
                </div>
                <p
                    data-testid="summary-dropdown-content-header-description"
                    className="mb-4 text-left font-inter text-xs font-normal leading-[18px]"
                >
                    {t('analytics.summary_dropdown.list.description')}
                </p>
            </div>
            <ul
                data-testid="summary-dropdown-content-list"
                ref={scrollableRef}
                className="grid max-h-filterContentMinHeight items-start gap-x-2 gap-y-2 overflow-y-scroll px-4"
                style={{ gridTemplateColumns: columnsTemplate }}
            >
                {summaryData.map(
                    ({
                        color,
                        dataKey,
                        currentDataSetSummary,
                        previousDataSetSummary,
                    }) => {
                        const dataKeyLowercased = dataKey
                            .toLowerCase()
                            .replace(/ /g, '-')

                        return (
                            <li
                                data-testid={`summary-dropdown-content-list-${dataKeyLowercased}`}
                                className="contents font-inter"
                                style={{ display: 'contents' }}
                                key={dataKey}
                            >
                                <div className="col-start-1 flex items-center gap-1">
                                    <span
                                        className="block h-2 w-2 shrink-0 rounded-full bg-[currentColor]"
                                        style={{ color }}
                                    />
                                    <span className="min-w-0 truncate whitespace-nowrap text-left text-xs font-medium leading-4">
                                        {dataKey}
                                    </span>
                                </div>
                                <span
                                    className="col-start-2"
                                    aria-hidden="true"
                                />
                                <span
                                    className="col-start-3 justify-self-end text-xs font-semibold leading-[18px]"
                                    data-testid={`summary-dropdown-content-list-${dataKeyLowercased}-value`}
                                >
                                    {formatNumber(currentDataSetSummary)}
                                </span>
                                {showVarianceBadge && (
                                    <VarianceBadge
                                        data-testid={`summary-dropdown-content-list-${dataKeyLowercased}-variance-badge`}
                                        classes="col-start-4 w-fit justify-self-end"
                                        current={currentDataSetSummary}
                                        previous={previousDataSetSummary}
                                    />
                                )}
                            </li>
                        )
                    }
                )}
                {totalUsns > 0 && (
                    <DataCompletenessListItem
                        instancesWithData={instancesWithData}
                        totalInstances={totalInstances}
                        completenessPercentage={completenessPercentage}
                        gridTemplateColumns={columnsTemplate}
                    />
                )}
            </ul>
        </div>
    )
}
